import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  Paper,
} from "@mui/material";
import { motion } from "framer-motion";
import axios from "api/axios";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "state";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DISABLED_ROLE_MESSAGE, isRoleDisabled } from "constants/authRoles";

// Google logo SVG component
const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
      fill="#4285F4"
    />
    <path
      d="M9.003 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.26c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z"
      fill="#34A853"
    />
    <path
      d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      fill="#FBBC05"
    />
    <path
      d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
      fill="#EA4335"
    />
  </svg>
);

const Login = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector((state) => state.global.user);

  const [error, setError] = useState("");

  // Check for OAuth errors from URL params
  useEffect(() => {
    const oauthError = searchParams.get("error");
    const oauthEmail = searchParams.get("email");

    if (oauthError) {
      switch (oauthError) {
        case "not_registered":
          setError(`No account found for ${oauthEmail || "this email"}. Please contact admin to register.`);
          break;
        case "no_email":
          setError("Could not get email from Google. Please try again.");
          break;
        case "oauth_failed":
          setError("Google sign-in failed. Please try again.");
          break;
        case "role_disabled":
          setError(DISABLED_ROLE_MESSAGE);
          break;
        case "session_expired":
          setError("You were signed out after 1 hour of inactivity. Please sign in again.");
          break;
        case "no_code":
          setError("Sign-in was cancelled or incomplete. Please try again.");
          break;
        default:
          setError("Sign-in failed. Please try again.");
      }
      navigate("/login", { replace: true });
    }
  }, [searchParams, navigate]);

  // Redirect if already logged in
  useEffect(() => {
    const checkAuth = async () => {
      if (user) {
        if (isRoleDisabled(user.role)) {
          setError(DISABLED_ROLE_MESSAGE);
          return;
        }
        navigate("/dashboard", { replace: true });
        return;
      }

      const fromLogout = sessionStorage.getItem("logout");
      if (fromLogout) {
        sessionStorage.removeItem("logout");
        return;
      }

      try {
        const res = await axios.get("/auth/me");
        if (res.data) {
          if (isRoleDisabled(res.data.role)) {
            setError(DISABLED_ROLE_MESSAGE);
            return;
          }
          dispatch(setUser(res.data));
          const role = res.data.role;
          navigate("/dashboard", { replace: true });
        }
      } catch (err) {
        if (err?.response?.status === 403) {
          setError(err?.response?.data?.message || DISABLED_ROLE_MESSAGE);
        }
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleSignIn = () => {
    const backendUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5001";
    window.location.href = `${backendUrl}/auth/google`;
  };

  return (
    <Box
      width="100%"
      height="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      className="scrollbar-page"
      sx={{
        backgroundColor: theme.palette.background.default,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        style={{ width: "100%", maxWidth: "450px" }}
      >
        <Paper
          elevation={5}
          sx={{
            p: "2.5rem",
            borderRadius: "1rem",
            backgroundColor: theme.palette.background.alt,
            position: "relative",
          }}
        >
          <Button
            sx={{
              position: "absolute",
              top: "1rem",
              left: "1rem",
              textTransform: "none",
              color: theme.palette.text.secondary,
              "&:hover": { color: theme.palette.text.primary },
            }}
            onClick={() => navigate("/home")}
          >
            ← Back
          </Button>

          <Typography variant="h4" textAlign="center" fontWeight="700" sx={{ mt: 2 }}>
            Sign in
          </Typography>
          <Typography variant="body2" textAlign="center" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Use your Google account to continue.
          </Typography>

          {error && (
            <Typography
              sx={{
                color: theme.palette.error.main,
                textAlign: "center",
                mb: "1rem",
              }}
            >
              {error}
            </Typography>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<GoogleLogo />}
            onClick={handleGoogleSignIn}
            sx={{
              py: "0.85rem",
              textTransform: "none",
              fontWeight: 600,
              bgcolor: theme.palette.mode === "dark" ? theme.palette.grey[100] : "#fff",
              color: theme.palette.mode === "dark" ? "#1a1a1a" : "#3c4043",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
              "&:hover": {
                bgcolor: theme.palette.mode === "dark" ? "#e8eaed" : "#f8f9fa",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              },
            }}
          >
            Continue with Google
          </Button>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default Login;
